import { APIs, Config } from '@ecdlink/core';
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { store } from '@store';
import { refreshToken } from '@store/auth/auth.actions';
import { queryErrorsActions } from '@/store/queryErrors';
import { sendQueryErrors } from '@/store/queryErrors/queryErrors.actions';

const disableGraphqlErrorAlert =
  process.env.REACT_APP_DISABLE_GRAPHQL_ERROR_ALERT;
const disableGraphqlLogging = process.env.REACT_APP_DISABLE_GRAPHQL_LOGGING;

const AUTH_ERROR_CODES = ['AUTH_NOT_AUTHORISED', 'AUTH_NOT_AUTHORIZED'];

type AuthRetryAxiosRequestConfig = AxiosRequestConfig & {
  _authRetryAttempted?: boolean;
};

let authReauthRequired = false;
let authReauthPromise: Promise<void> | null = null;
let resolveAuthReauthPromise: (() => void) | null = null;

const getAuthReauthPromise = (): Promise<void> => {
  if (!authReauthPromise) {
    authReauthPromise = new Promise((resolve) => {
      resolveAuthReauthPromise = resolve;
    });
  }

  return authReauthPromise;
};

const releaseAuthReauthLock = () => {
  authReauthRequired = false;
  if (resolveAuthReauthPromise) {
    resolveAuthReauthPromise();
    resolveAuthReauthPromise = null;
  }
  authReauthPromise = null;
};

const enterAuthReauthFlow = () => {
  if (authReauthRequired) return;

  authReauthRequired = true;
  getAuthReauthPromise();
  window.dispatchEvent(
    new CustomEvent('auth-not-authorised', {
      detail: { code: 'AUTH_NOT_AUTHORISED' },
    })
  );
};

window.addEventListener('auth-reauthenticated', releaseAuthReauthLock);

const logGraphQL = (
  logFunc: (message?: string, ...optionalParams: any[]) => void,
  statusText: any,
  status: any,
  query: any,
  result: any
) => {
  if (!!disableGraphqlLogging) return;
  // logFunc(`GRAPHQL: ${statusText}[${status}] `, {
  //   query: query,
  //   result: result,
  // });
};

const alertGraphQL = () => {
  if (!!disableGraphqlErrorAlert) return;
  window.dispatchEvent(new CustomEvent('graphql-error', {})); // AppErrorHandler listens for the event.
};

const alertTimeout = () => {
  if (!!disableGraphqlErrorAlert) return;
  window.dispatchEvent(new CustomEvent('timeout-error', {})); // AppErrorHandler listens for the event.
};

const getGraphQlAuthErrorCode = (payload: any): string | undefined => {
  const errors = payload?.data?.errors;

  if (!Array.isArray(errors)) {
    return undefined;
  }

  const authError = errors.find((error: any) =>
    AUTH_ERROR_CODES.includes(error?.extensions?.code)
  );

  return authError?.extensions?.code;
};

export const api = (baseUrl: string, token?: string): AxiosInstance => {
  const blackList = [
    APIs.authLogin,
    APIs.refreshJwtToken,
    APIs.acceptInvitation,
    APIs.confirmForgotPasswordReset,
    APIs.forgotPassword,
    APIs.sendAuthCode,
    APIs.verifyInvitation,
  ];
  const headers: any = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const axiosInstance = axios.create({
    baseURL: baseUrl,
    headers,
  });

  axiosInstance.interceptors.request.use(
    async (config) => {
      if (!navigator.onLine) {
        return new Promise((resolve) => {
          resolve({
            data: null,
            status: 'offline',
            statusText: 'Browser is offline',
          });
        });
      }

      if (
        authReauthRequired &&
        !blacklistCheckup(config.url ?? '', blackList)
      ) {
        await getAuthReauthPromise();
      }

      if (store && !blacklistCheckup(config.url ?? '', blackList)) {
        const user = store?.getState()?.auth?.userAuth;

        let currentDate = new Date();
        if (user?.auth_token) {
          const decodedToken: { exp: number } = jwt_decode(user?.auth_token);
          if (decodedToken.exp * 1000 < currentDate.getTime()) {
            await store.dispatch(refreshToken({}));
          }

          // Always set the latest token from store so retried requests do not use stale auth headers.
          if (!config.headers) {
            config.headers = {};
          }

          config.headers['Authorization'] = `Bearer ${
            store?.getState()?.auth?.userAuth?.auth_token
          }`;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse<any, any>) => {
      if (response.config.baseURL === Config.graphQlApi) {
        const authErrorCode = getGraphQlAuthErrorCode(response);
        if (authErrorCode) {
          enterAuthReauthFlow();

          const requestConfig = response.config as AuthRetryAxiosRequestConfig;
          if (
            !requestConfig._authRetryAttempted &&
            !blacklistCheckup(requestConfig.url ?? '', blackList)
          ) {
            requestConfig._authRetryAttempted = true;
            await getAuthReauthPromise();
            return axiosInstance.request(requestConfig);
          }

          return response;
        }

        // checked for internal exception.
        if (response.data.errors === undefined) {
          logGraphQL(
            console.log,
            response.request.statusText,
            response.request.status,
            response.config.data,
            response.data
          );
        } else {
          (response as any).original_status = response.status;
          (response as any).original_statusText = response.statusText;
          response.status = 500;
          response.statusText =
            'Internal Server Error' +
            (response.data.errors?.length > 0 &&
            response.data.errors[0] &&
            response.data.errors[0].message
              ? ': ' + response.data.errors[0].message
              : '');

          const user = store?.getState()?.auth?.userAuth;
          if (
            process.env.REACT_APP_SUPPRESS_ERROR_LOGGING === undefined ||
            process.env.REACT_APP_SUPPRESS_ERROR_LOGGING === '1'
          ) {
            for (let err of response.data.errors) {
              store.dispatch(
                queryErrorsActions.addError({
                  ...err,
                  timestamp: Date.now(),
                  eventDate: Date.now(),
                })
              );
            }
            await store.dispatch(sendQueryErrors(user?.id || ''));
          }
          logGraphQL(
            console.error,
            response.statusText,
            response.status,
            response.config.data,
            response.data
          );
        }
        if (response.status >= 400) {
          if (response.status === 408 || response.status === 504) {
            alertTimeout();
          } else {
            alertGraphQL();
          }
        }
      }
      return response;
    },
    (error: AxiosError) => {
      if (error.config.baseURL === Config.graphQlApi) {
        const authErrorCode = getGraphQlAuthErrorCode(error.response);
        if (authErrorCode) {
          enterAuthReauthFlow();

          const requestConfig = error.config as AuthRetryAxiosRequestConfig;
          if (
            !requestConfig._authRetryAttempted &&
            !blacklistCheckup(requestConfig.url ?? '', blackList)
          ) {
            requestConfig._authRetryAttempted = true;
            return getAuthReauthPromise().then(() =>
              axiosInstance.request(requestConfig)
            );
          }

          return Promise.reject(error);
        }

        logGraphQL(
          console.error,
          error.request.statusText,
          error.request.status,
          error.config.data,
          error.response?.data
        );
        if (error.message === 'Network Error') {
          alertTimeout();
        } else {
          alertGraphQL();
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

const blacklistCheckup = ($url: string, blacklist: string[]): boolean => {
  let returnValue = false;
  blacklist.forEach((i) => {
    if ($url.includes(i)) {
      returnValue = true;
    }
  });
  return returnValue;
};
